import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Button, SecondaryButton, Container } from '../ui/Primitives'
import { LanguageSwitcher } from '../ui/LanguageSwitcher'
import DhtnLogo from '../ui/DhtnLogo'
import { useAuth } from '../../contexts/authContext'
import { useI18n } from '../../i18n/I18nContext'
import { useTheme } from '../../contexts/ThemeContext'
import ThemeToggle from '../ui/ThemeToggle'
import './PublicHeader.css'

interface PublicHeaderProps {
    fixed?: boolean
    surface?: 'default' | 'light' | 'dark'
    /** Landing only: show the language switcher (swapped for the CTA on scroll). Every
        other public page hides the switcher and shows the [Start learning] + [Log in] pair. */
    showLanguage?: boolean
    /** Landing only (with showLanguage, logged out): while `after` is scrolled up out of
        view, the switcher crossfades into a Start-learning CTA carrying `label`. */
    swapToCta?: { after: React.RefObject<HTMLElement | null>; label: string }
    /** Bottom border. Pass false to hide it; 'scrolled' shows it only once the page has
        scrolled (landing). Defaults to true so other pages keep their divider. */
    showBorder?: boolean | 'scrolled'
    /** Landing only: once `hideAfter` has scrolled up behind the header (so the next
        section's top meets the header's bottom), the header slides out and goes inert. */
    hideAfter?: React.RefObject<HTMLElement | null>
    /** Catalog exception: on /classes the primary "Start learning" would just scroll
        to top (this page IS the destination), so logged-out shows [Log in] only. The
        persistent [Start learning] + [Log in] pair still stands on every other page. */
    hideStartLearningCta?: boolean
    /** Task pages keep language and theme reachable beside account access. */
    showPreferences?: boolean
    /** Focused task screens provide their account/navigation actions in the main content. */
    showAccountAccess?: boolean
}

/** True once `target` has scrolled up behind the header: its bottom is above the header's
    bottom. One observer, no scroll listener; the header's bottom edge is read once. */
function useGoneBehind(target: React.RefObject<HTMLElement | null> | undefined, header: React.RefObject<HTMLElement | null>) {
    const [gone, setGone] = useState(false)
    useEffect(() => {
        const el = target?.current
        if (!el) return
        const headerBottom = Math.round(header.current?.getBoundingClientRect().bottom ?? 0)
        const io = new IntersectionObserver(([entry]) => {
            const rootTop = entry.rootBounds?.top ?? headerBottom
            setGone(!entry.isIntersecting && entry.boundingClientRect.top < rootTop)
        }, { rootMargin: `-${headerBottom}px 0px 0px 0px` })
        io.observe(el)
        return () => io.disconnect()
    }, [target, header])
    return gone
}

const PublicHeader: React.FC<PublicHeaderProps> = ({
    fixed = false,
    surface = 'default',
    showLanguage = false,
    swapToCta,
    showBorder = true,
    hideAfter,
    hideStartLearningCta = false,
    showPreferences = false,
    showAccountAccess = true,
}) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { user, isLoading } = useAuth()
    const { t } = useI18n()
    const { isDark } = useTheme()
    const headerRef = useRef<HTMLElement>(null)
    const topRef = useRef<HTMLDivElement>(null)
    const [scrolled, setScrolled] = useState(false)
    const ctaShown = useGoneBehind(swapToCta?.after, headerRef)
    const hidden = useGoneBehind(hideAfter, headerRef)
    const borderOnScroll = showBorder === 'scrolled' && !fixed

    // Border after any scroll: watch the zero-height spacer that sits above the header.
    useEffect(() => {
        const sentinel = topRef.current
        if (!borderOnScroll || !sentinel) return
        const io = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting))
        io.observe(sentinel)
        return () => io.disconnect()
    }, [borderOnScroll])

    const isLightSurface = surface === 'light'
    const isDarkSurface = surface === 'dark'
    const surfaceBg = isDarkSurface
        ? 'bg-[#0a0f1a] text-white'
        : isLightSurface
          ? 'bg-white text-[#0E1014]'
          : 'bg-[var(--dhtn-canvas)]'
    const borderColor = !showBorder || (borderOnScroll && !scrolled)
        ? 'border-transparent'
        : borderOnScroll
          ? 'border-[var(--dhtn-border)]'
        : isDarkSurface
          ? 'border-white/15'
          : isLightSurface
            ? 'border-[#E1E6F1]'
            : 'border-[var(--dhtn-border)]'
    const headerClass = `${borderColor} ${surfaceBg}`
    const primaryVariant = isDarkSurface
        ? 'whiteDark'
        : isLightSurface
          ? 'primary'
          : isDark
            ? 'primaryDark'
            : 'primary'
    const logoSurface = isDarkSurface || (!isLightSurface && isDark) ? 'dark' : 'light'
    const logoFocusClass = isDarkSurface
        ? 'focus-visible:ring-white'
        : 'focus-visible:ring-[var(--dhtn-text)]'

    // Primary CTA → the catalog. On /classes itself, scroll up instead of re-navigating.
    const goStartLearning = () => {
        if (location.pathname === '/classes') {
            window.scrollTo({ top: 0, behavior: 'smooth' })
        } else {
            navigate('/classes')
        }
    }

    const switcher = (
        <LanguageSwitcher
            variant={isDarkSurface || (!isLightSurface && isDark) ? 'dark' : 'light'}
            forceLight={isLightSurface}
            forceDark={isDarkSurface}
        />
    )

    return (
        <>
            {!fixed && <div ref={topRef} aria-hidden="true" style={{ height: 'var(--dhtn-banner-offset, 0px)' }} />}
            <header
                ref={headerRef}
                data-public-header
                inert={hidden}
                className={`${fixed ? 'fixed inset-x-0' : 'sticky'} top-[var(--dhtn-banner-offset,0px)] z-50 border-b-2 transition-[transform,opacity,background-color,border-color] duration-200 ease-out ${hidden ? '-translate-y-full opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'} ${headerClass}`}
            >
            <Container className={`flex min-h-[72px] items-center justify-between ${showPreferences ? 'gap-2 sm:gap-4' : 'gap-4'} py-3 md:min-h-20 md:py-0`}>
                <div className="flex shrink-0 items-center">
                    <Link
                        to="/"
                        aria-label={t('landingPage.aria.goToHomepage')}
                        className={`inline-flex min-h-[44px] min-w-[44px] items-center gap-[9px] rounded-sm focus-visible:outline-none focus-visible:ring-2 ${logoFocusClass}`}
                    >
                        {/* The mark alone, with its quiet Shine (founder, 10 Oct 2026). */}
                        <DhtnLogo className="public-header-logo" surface={logoSurface} variant="symbol" shine="rest" />
                    </Link>
                </div>

                <div className="flex items-center justify-end gap-2 sm:gap-3">
                    {showPreferences && <><ThemeToggle /><LanguageSwitcher compactMobile variant={isDark ? 'dark' : 'light'} /></>}
                    {!showAccountAccess ? null : !isLoading && user ? (
                        /* Logged in → dashboard */
                        <Button
                            variant={primaryVariant}
                            size="sm"
                            fullWidth={false}
                            className={showPreferences ? '!min-h-[44px] !px-3 sm:!px-6' : '!min-h-[44px]'}
                            onClick={() => navigate('/dashboard')}
                        >
                            {t('footer.navDashboard')}
                        </Button>
                    ) : showLanguage ? (
                        /* Landing → switcher; with swapToCta it crossfades to the CTA on scroll.
                           Both sit stacked in one grid cell, so the slot keeps the wider width. */
                        swapToCta ? (
                            <div className="public-header-swap" data-cta-shown={ctaShown}>
                                <div inert={ctaShown}>{switcher}</div>
                                <div inert={!ctaShown}>
                                    <Button
                                        variant={primaryVariant}
                                        size="sm"
                                        fullWidth={false}
                                        className="!min-h-[44px] whitespace-nowrap"
                                        onClick={goStartLearning}
                                    >
                                        {swapToCta.label}
                                    </Button>
                                </div>
                            </div>
                        ) : switcher
                    ) : (
                        /* Every other public page, logged out → persistent Start learning + Log in */
                        <>
                            <SecondaryButton
                                variant={isDarkSurface ? 'secondaryOnColor' : 'secondary'}
                                size="sm"
                                fullWidth={false}
                                className={showPreferences ? '!min-h-[44px] !px-3 sm:!px-6' : '!min-h-[44px]'}
                                onClick={() => navigate('/login')}
                            >
                                {t('landingPage.signIn')}
                            </SecondaryButton>
                            {!hideStartLearningCta && (
                                <Button
                                    variant={primaryVariant}
                                    size="sm"
                                    fullWidth={false}
                                    className="!min-h-[44px]"
                                    onClick={goStartLearning}
                                >
                                    {t('landingPage.getStarted')}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </Container>
        </header>
        </>
    )
}

export default PublicHeader
